import { createClient } from '@supabase/supabase-js';

// Default values if environment variables aren't set
// These should be replaced with your actual Supabase credentials in production
const DEFAULT_SUPABASE_URL = 'https://your-supabase-project.supabase.co';
const DEFAULT_SUPABASE_KEY = 'your-public-anon-key';
const DEFAULT_SUPABASE_SERVICE_KEY = 'your-service-role-key';

// Get Supabase URL and key from environment variables or use defaults
export const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || DEFAULT_SUPABASE_URL;
export const supabaseKey = process.env.REACT_APP_SUPABASE_ANON_KEY || DEFAULT_SUPABASE_KEY;
export const supabaseServiceKey = process.env.REACT_APP_SUPABASE_SERVICE_KEY || DEFAULT_SUPABASE_SERVICE_KEY;

// Create Supabase client with custom options
export const supabase = createClient(supabaseUrl, supabaseKey, {
  auth: {
    persistSession: false // Don't persist session in browser storage
  }
});

// Create admin client with service role key for bypassing RLS
export const supabaseAdmin = createClient(supabaseUrl, supabaseServiceKey, {
  auth: {
    persistSession: false
  }
});

// Function to check if Supabase is properly configured
export function isSupabaseConfigured(): boolean {
  return (
    supabaseUrl !== DEFAULT_SUPABASE_URL && 
    supabaseKey !== DEFAULT_SUPABASE_KEY
  );
}

// Function to check if Supabase Admin is properly configured
export function isSupabaseAdminConfigured(): boolean {
  return (
    supabaseUrl !== DEFAULT_SUPABASE_URL && 
    supabaseServiceKey !== DEFAULT_SUPABASE_SERVICE_KEY
  );
}

/**
 * Generate a properly signed URL using Supabase's createSignedUrl method
 * This creates a URL with the format:
 * https://[project].supabase.co/storage/v1/object/sign/[bucket]/[filename]?token=[token]
 * 
 * @param bucket - The bucket name
 * @param folder - The folder name within the bucket
 * @param filename - The filename to generate a signed URL for
 * @param expiresIn - Number of seconds until the URL expires (default: 24 hours)
 * @returns A Promise resolving to the signed URL string or null if error
 */
export async function createSignedUrl(
  bucket: string, 
  folder: string, 
  filename: string, 
  expiresIn: number = 86400
): Promise<string | null> {
  try {
    // Try different path variations to account for how the file might be stored
    const possiblePaths = [
      `${folder}/${filename}`,      // materials/filename.pdf
      filename,                     // filename.pdf
      `${bucket}/${folder}/${filename}`, // tls/materials/filename.pdf
    ];
    
    console.log(`Attempting to create signed URL with multiple path variations for ${filename}`);
    
    // Try each path variation until one works
    for (const path of possiblePaths) {
      console.log(`Trying path: ${path}`);
      
      const { data, error } = await supabase
        .storage
        .from(bucket)
        .createSignedUrl(path, expiresIn);
      
      if (!error && data && data.signedUrl) {
        console.log(`Success! Generated signed URL with path: ${path}`);
        return data.signedUrl;
      } else {
        console.log(`Failed with path ${path}: ${error?.message || 'Unknown error'}`);
      }
    }
    
    // If we're here, all attempts failed - try a fallback approach for public URLs
    console.log("All signed URL attempts failed. Trying to generate a public URL instead...");
    
    const { data: publicData } = supabase
      .storage
      .from(bucket)
      .getPublicUrl(`${folder}/${filename}`);
    
    if (publicData && publicData.publicUrl) {
      console.log("Generated public fallback URL");
      return publicData.publicUrl;
    }
    
    // If all attempts fail
    console.error("All URL generation attempts failed for file:", filename);
    return null;
  } catch (err) {
    console.error('Exception creating URL:', err);
    return null;
  }
}

// Function to get the bucket name
export const BUCKET_NAME = 'tls';

// Function to get the material folder path
export const MATERIALS_FOLDER = 'materials';

// Flag to indicate whether we're using S3 protocol for uploads
export const useS3Protocol = true; 